// Mock API Service para desenvolvimento
// Este arquivo simula as respostas da API quando o backend não está disponível

interface MockUser {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  role: 'admin' | 'locador' | 'locatario';
  cpf_cnpj?: string;
  created_at: string;
}

interface MockApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

class MockApiService {
  private users: MockUser[] = [
    {
      id: '1',
      nome: 'Admin Teste',
      email: 'admin@teste.com',
      telefone: '(11) 99999-9999',
      role: 'admin',
      cpf_cnpj: '123.456.789-00',
      created_at: '2024-01-01T00:00:00Z'
    },
    {
      id: '2',
      nome: 'Locador Teste',
      email: 'locador@teste.com',
      telefone: '(11) 88888-8888',
      role: 'locador',
      cpf_cnpj: '987.654.321-00',
      created_at: '2024-01-01T00:00:00Z'
    },
    {
      id: '3',
      nome: 'Locatário Teste',
      email: 'locatario@teste.com',
      telefone: '(11) 77777-7777',
      role: 'locatario',
      cpf_cnpj: '456.789.123-00',
      created_at: '2024-01-01T00:00:00Z'
    }
  ];

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async login(email: string, senha: string): Promise<MockApiResponse> {
    await this.delay(1000); // Simular delay da rede

    const user = this.users.find(u => u.email === email);
    
    if (!user) {
      return {
        success: false,
        message: 'Usuário não encontrado'
      };
    }

    // Simular senha válida (qualquer senha funciona no mock)
    if (senha.length < 3) {
      return {
        success: false,
        message: 'Senha inválida'
      };
    }

    const token = `mock_token_${user.id}_${Date.now()}`;

    return {
      success: true,
      message: 'Login realizado com sucesso',
      data: {
        usuario: user,
        token: token
      }
    };
  }

  async register(userData: {
    nome: string;
    email: string;
    senha: string;
    telefone: string;
    role: 'locador' | 'locatario';
  }): Promise<MockApiResponse> {
    await this.delay(1000);

    // Verificar se email já existe
    const existingUser = this.users.find(u => u.email === userData.email);
    if (existingUser) {
      return {
        success: false,
        message: 'Email já cadastrado'
      };
    }

    const newUser: MockUser = {
      id: (this.users.length + 1).toString(),
      nome: userData.nome,
      email: userData.email,
      telefone: userData.telefone,
      role: userData.role,
      created_at: new Date().toISOString()
    };

    this.users.push(newUser);

    return {
      success: true,
      message: 'Usuário cadastrado com sucesso',
      data: newUser
    };
  }

  async getProfile(): Promise<MockApiResponse> {
    await this.delay(500);

    // Simular usuário logado (admin por padrão)
    const user = this.users[0];

    return {
      success: true,
      message: 'Perfil carregado com sucesso',
      data: user
    };
  }

  async updateProfile(userData: Partial<{
    nome: string;
    telefone: string;
    cpf_cnpj: string;
  }>): Promise<MockApiResponse> {
    await this.delay(500);

    const user = this.users[0];
    const updatedUser = { ...user, ...userData };

    return {
      success: true,
      message: 'Perfil atualizado com sucesso',
      data: updatedUser
    };
  }

  async getImoveis(filters: any = {}): Promise<MockApiResponse> {
    await this.delay(800);

    const mockImoveis = [
      {
        id: '1',
        titulo: 'Apartamento 3 quartos no Centro',
        descricao: 'Apartamento bem localizado no centro da cidade',
        endereco: 'Rua das Flores, 123',
        cidade: 'São Paulo',
        estado: 'SP',
        bairro: 'Centro',
        tipo: 'apartamento',
        quartos: 3,
        banheiros: 2,
        vagas_garagem: 1,
        area: 85,
        valor: 2500,
        aceita_pet: true,
        mobiliado: false,
        ar_condicionado: true,
        internet: true,
        comodidades: ['Piscina', 'Academia', 'Portaria 24h'],
        status: 'aprovado',
        created_at: '2024-01-01T00:00:00Z'
      },
      {
        id: '2',
        titulo: 'Casa 4 quartos com quintal',
        descricao: 'Casa espaçosa com quintal para crianças',
        endereco: 'Av. Principal, 456',
        cidade: 'São Paulo',
        estado: 'SP',
        bairro: 'Vila Nova',
        tipo: 'casa',
        quartos: 4,
        banheiros: 3,
        vagas_garagem: 2,
        area: 150,
        valor: 3500,
        aceita_pet: true,
        mobiliado: true,
        ar_condicionado: false,
        internet: true,
        comodidades: ['Quintal', 'Garagem', 'Área de churrasco'],
        status: 'aprovado',
        created_at: '2024-01-02T00:00:00Z'
      }
    ];

    return {
      success: true,
      message: 'Imóveis carregados com sucesso',
      data: {
        imoveis: mockImoveis,
        pagination: {
          page: 1,
          limit: 100,
          total: mockImoveis.length,
          totalPages: 1
        }
      }
    };
  }

  async getImovel(id: string): Promise<MockApiResponse> {
    await this.delay(500);

    const imovel = {
      id: id,
      titulo: 'Apartamento 3 quartos no Centro',
      descricao: 'Apartamento bem localizado no centro da cidade',
      endereco: 'Rua das Flores, 123',
      cidade: 'São Paulo',
      estado: 'SP',
      bairro: 'Centro',
      tipo: 'apartamento',
      quartos: 3,
      banheiros: 2,
      vagas_garagem: 1,
      area: 85,
      valor: 2500,
      aceita_pet: true,
      mobiliado: false,
      ar_condicionado: true,
      internet: true,
      comodidades: ['Piscina', 'Academia', 'Portaria 24h'],
      status: 'aprovado',
      created_at: '2024-01-01T00:00:00Z'
    };

    return {
      success: true,
      message: 'Imóvel carregado com sucesso',
      data: imovel
    };
  }

  async checkHealth(): Promise<MockApiResponse> {
    return {
      success: true,
      message: 'API Mock funcionando',
      data: { status: 'ok', timestamp: new Date().toISOString() }
    };
  }
}

export const mockApiService = new MockApiService();
export default mockApiService;
